import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { TextField } from '../../../lib/mui-rff/TextField';
import MultiCardDisplay from './stepComponents/MultiCardDisplay';
import { CardConfigurerStepProps } from '../CardConfigurerForm';
import { isValidMessage, composeValidators } from 'utilities/regEx';
import { UPLOAD_HELPER_TEXT, CARD_MEDIUM_DIGITAL } from 'utilities/constants';
import FormSection from 'components/Layout/FormSection';
import FormUploadField from 'components/Form/UploadFieldToS3';
import Button from '@mui/material/Button';
import FormStepDialog from './stepComponents/FormStepDialog';
import VideoPreview from './stepComponents/VideoPreview';
import CardReveal from 'components/Card/CardReveal';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/CancelRounded';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { canUploadVideo } from '../configurerFlags';
import HelperIcon from 'components/Helpers/HelperIcon';
import FormLabel from '@mui/material/FormLabel';
import { isMobileOrTablet } from 'utilities/userAgent';
import { isCardDotGiftCard } from '../configurerFlags';
import { useProduct } from 'effects/useProduct';
import PanelLayout from './stepComponents/PanelLayout';

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      '&[hidden]': {
        display: 'none',
      },
    },
    field: {
      marginTop: theme.spacing(2),
    },
    fieldLabel: {
      ...theme.typography.h3,
      transform: 'translate(0,-16px)',
      color: 'white',
    },
    standaloneLabel: {
      ...theme.typography.h3,
      color: 'white',
      marginBottom: theme.spacing(2),
    },
  }),
  { name: 'CardMessage' },
);

const CardMessage: React.FC<CardConfigurerStepProps> = ({ step, index, product, values, form, submitButton }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showUnboxing, setShowUnboxing] = useState(false);
  const showVideoUpload = canUploadVideo(values, product.productType);
  const classes = useStyles();
  const { product: cardDesignProduct } = useProduct(values.cardDesignHandle);

  const title = 'Would you like to include a message?';

  const cardImageContent = <MultiCardDisplay defaultProductHandle={product.handle} values={values} showLogo={!!values.cardCustomImage && !isCardDotGiftCard(product)} />;

  const formContent = (
    <Stack spacing={4}>
      <FormSection sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}>
        {values.recipientCount === 'one' && (
          <TextField
            className={classes.field}
            fullWidth
            variant="standard"
            label="Add a written message"
            placeholder="Write a personal message..."
            name="recipientMessage"
            multiline
            InputLabelProps={{ shrink: true, className: classes.fieldLabel }}
            fieldProps={{
              validate: composeValidators(isValidMessage),
            }}
            inputProps={{ maxLength: 150 }}
          />
        )}
        {showVideoUpload && (
          <Box sx={{ width: { xs: '100%', sm: 'auto' }, px: '9px' }}>
            <FormLabel component="div" className={classes.standaloneLabel}>
              Add a video message{' '}
              <HelperIcon
                color="secondary"
                message="Include a personal video message alongside your card. Either record live or upload a prerecorded video from your device. Maximum video size is 50MB, maximum length is 60 seconds"
              />
            </FormLabel>
            {values.recipientVideo &&
              // @ts-ignore
              values.recipientVideo.key && (
                <>
                  <Stack spacing={1} direction="column">
                    <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack spacing={1} direction="row" alignItems="center">
                        <Typography color="success.main">
                          <CheckIcon />
                        </Typography>
                        <Typography variant="subtitle1" sx={{ minHeight: 20 }}>
                          Video uploaded!
                        </Typography>
                      </Stack>
                      <IconButton edge="end" onClick={() => form.change('recipientVideo', undefined)} size="large">
                        <RemoveIcon />
                      </IconButton>
                    </Stack>
                    <Button fullWidth size="medium" variant="outlined" onClick={() => setShowPreview(true)}>
                      Preview Video
                    </Button>
                  </Stack>
                  <FormStepDialog open={showPreview} onClose={() => setShowPreview(false)}>
                    <VideoPreview
                      // the video preview
                      videoURL={(values.recipientVideo && values.recipientVideo.url) || undefined}
                      onConfirm={() => setShowPreview(false)}
                    />
                  </FormStepDialog>
                </>
              )}
            <FormUploadField
              // Video Upload/Record
              name="recipientVideo"
              type="video"
              allowRecordVideo
              accept="video/mp4,video/quicktime"
              helperText={UPLOAD_HELPER_TEXT}
              hideOnValid
              label={isMobileOrTablet() ? 'Upload / Record Video' : 'Upload Video'}
              mobileMode={isMobileOrTablet()}
            />
          </Box>
        )}
        {values.cardMedium === CARD_MEDIUM_DIGITAL && (
          <>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Button variant="text" onClick={() => setShowUnboxing(true)}>
                Preview Unboxing
              </Button>
              <HelperIcon color="secondary" message="Preview the experience your recipients will recieve when you send their card!" />
            </Box>
            <FormStepDialog open={showUnboxing} onClose={() => setShowUnboxing(false)}>
              <Box sx={{ flex: '1 1 auto' }}>
                <CardReveal
                  item={cardDesignProduct || product}
                  message={values.recipientMessage}
                  // cardValue={values.cardValue}
                />
              </Box>
            </FormStepDialog>
          </>
        )}
      </FormSection>
    </Stack>
  );

  return (
    <div className={classes.root} hidden={step !== index}>
      <PanelLayout value={step} index={index} title={title} cardImageContent={cardImageContent} formContent={formContent} buttonsContent={submitButton} />
    </div>
  );
};

export default CardMessage;
